import { useEffect, useRef, useState } from "react";
import { cn, fmt } from "@/src/lib/utils";
import { useAccount } from "wagmi";
import { Button, Card } from "@/src/components";
import { ConnectButton } from "@/src/components/connectButton";
import { useBalances } from "@/src/hooks/useBalances";
import { useStake } from "@/src/hooks/useStake";
import { toast } from "react-hot-toast";
import { activeChain } from "@/src/configs/chainConfig";
import { useDaoVotes } from "@/src/hooks/useVotes";

export const Stake = () => {
  const { chain } = useAccount();
  const { onStake, onUnstake } = useStake();
  const { isConnected } = useAccount();
  const { balance, stakeBalance } = useBalances();
  const { eligibleVotes, vote_enabled, deadline } = useDaoVotes();
  const submitButtonRef = useRef(null);

  const [{ isCorrectChain }, setState] = useState({
    isCorrectChain: true,
  });
  useEffect(() => {
    if (isConnected)
      setState({
        isCorrectChain: chain?.id === Number(activeChain.chainId),
      });

  }, [chain, isConnected]);

  const [{ isStake, placeholder }, setOption] = useState({
    isStake: true,
    placeholder: "Amount to stake",
  });

  const [{ stakeInput, unstakeInput }, setInput] = useState({
    stakeInput: "",
    unstakeInput: "",
  });
  useEffect(() => {
    submitButtonRef.current &&
      (() => {
        submitButtonRef.current.disabled = true;
        if (!isCorrectChain) {
          submitButtonRef.current.innerHTML = "Wrong Network";
          return;
        }

        if (isStake) {
          if (balance === 0 || stakeInput > balance) {
            submitButtonRef.current.innerHTML = "insufficient balance";
            return;
          }
          if (stakeInput) {
            submitButtonRef.current.innerHTML = "Stake";
            submitButtonRef.current.disabled = false;
            return;
          }
          submitButtonRef.current.innerHTML = "Enter quantity";

          return;
        }

        if (stakeBalance === 0 || unstakeInput > stakeBalance) {
          submitButtonRef.current.innerHTML = "insufficient balance";
          submitButtonRef.current.disabled = true;
          return;
        }
        if (unstakeInput) {
          submitButtonRef.current.innerHTML = "Unstake (5% LP FEE)";
          submitButtonRef.current.disabled = false;
          return;
        }
        submitButtonRef.current.innerHTML = "Enter quantity";
          return;
      })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStake, stakeInput, unstakeInput, balance, stakeBalance]);

  const onInputChange = (event) => {
    const value = event.target.valueAsNumber;
    const _stakeInput = isStake ? value : stakeInput;
    const _unstakeInput = isStake ? unstakeInput : value;
    setInput({
      stakeInput: _stakeInput,
      unstakeInput: _unstakeInput,
    });
  };

  const onUnstakeInput = () => {
    setOption({
      isStake: false,
      placeholder: "Amount to unstake",
    });
  };
  const onStakeInput = () => {
    setOption({
      isStake: true,
      placeholder: "Amount to stake",
    });
  };

  const onMaxBalance = () => {
    if (isStake)
      return setInput((prev) => ({
        ...prev,
        stakeInput: balance,
      }));
    setInput((prev) => ({
      ...prev,
      unstakeInput: stakeBalance,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isStake)
      return toast.promise(onStake(stakeInput), {
        loading: "Transaction is in progress",
        success: "Successfully staked LP tokens",
        error: (data) => data,
      });

    toast.promise(onUnstake(unstakeInput), {
      loading: "Transaction is in progress",
      success: "Succesfully unstaked LP tokens",
      error: (data) => data,
    });
  };
  return (
    <div className="max-w-xl mx-auto w-full space-y-3">
      {(eligibleVotes > 0 && vote_enabled && Date.now()/1000 < deadline) &&
        <div className="border border-warning/30 bg-warning/5 rounded-xl px-4 py-3 flex items-start gap-3">
          <span className="text-warning text-lg leading-none mt-0.5">⚠</span>
          <p className="text-warning/90 text-sm font-medium">
            You still have {fmt(eligibleVotes, 0)} votes to decide on the pending proposal!
          </p>
        </div>
      }

      <Card>
        {/* Tab-style toggle */}
        <div className="flex bg-secondary/50 rounded-xl p-1 gap-1">
          <button
            type="button"
            onClick={onStakeInput}
            className={cn(
              "flex-1 py-2.5 px-2 sm:px-4 rounded-lg text-sm font-semibold transition-all duration-200",
              isStake
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Stake
          </button>
          <button
            type="button"
            onClick={onUnstakeInput}
            className={cn(
              "flex-1 py-2.5 px-2 sm:px-4 rounded-lg text-sm font-semibold transition-all duration-200",
              !isStake
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Unstake
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          {/* Input section */}
          <div>
            <label className="text-sm text-muted-foreground uppercase tracking-wider font-medium mb-2 block">
              {isStake ? "Stake Amount" : "Unstake Amount"}
            </label>
            <div className="flex w-full">
              <input
                value={isStake ? stakeInput : unstakeInput}
                max={isStake ? balance : stakeBalance}
                onChange={onInputChange}
                placeholder={placeholder}
                className="bg-secondary/50 border border-border rounded-xl rounded-r-none border-r-0 px-3 sm:px-4 py-3 text-sm text-foreground font-mono font-medium flex-1 min-w-0 hover:border-primary/40 focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none transition-all"
                type="number"
                required
              />
              <span className="w-1/5">
                <Button
                  type="button"
                  onClick={onMaxBalance}
                  className="rounded-l-none rounded-r-xl h-full text-xs"
                  variant="secondary"
                >
                  max
                </Button>
              </span>
            </div>
          </div>

          {/* Stats section */}
          <div className="bg-secondary/30 rounded-xl px-4 py-1">
            <div className="flex items-center justify-between py-2.5 border-b border-border/50">
              <span className="text-sm text-muted-foreground">Unstaked LP Tokens</span>
              <span className="text-sm font-bold text-foreground font-mono min-w-0 truncate">{fmt(balance)}</span>
            </div>
            <div className={cn(
              "flex items-center justify-between py-2.5",
              !isStake ? "border-b border-border/50" : ""
            )}>
              <span className="text-sm text-muted-foreground">Staked LP Tokens</span>
              <span className="text-sm font-bold text-foreground font-mono min-w-0 truncate">{fmt(stakeBalance)}</span>
            </div>
            {!isStake &&
              <div className="flex items-center justify-between py-2.5">
                <span className="text-sm text-muted-foreground">Harvest Points Fee</span>
                <span className="text-sm font-bold text-warning font-mono">10%</span>
              </div>
            }
          </div>

          {/* Submit section */}
          <div className="pt-1">
            {!isConnected && (
              <div className="flex justify-center">
                <ConnectButton />
              </div>
            )}
            {isConnected && <Button ref={submitButtonRef} type="submit">Stake</Button>}
          </div>
        </form>
      </Card>
    </div>
  );
};
